import { useEffect, useState } from "react";
import { masterApi, apiError } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Plus, Play, Pencil, Trash2, Eye, History, Save } from "lucide-react";

const SEGMENTOS = {
  sumidos: "Clientes Sumidos",
  primeiro_nao_voltou: "1º Cadastro sem retorno",
  frequentes: "Frequentes (alto)",
  low_freq: "Baixa Frequência",
  saudade_enviado: "Já receberam saudade",
  todos: "Todos os clientes",
};
const FREQUENCIAS = { diaria: "Diária", semanal: "Semanal", mensal: "Mensal" };
const DIAS_SEMANA = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

export default function MasterCampanhas() {
  const { unidades } = useUnit();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const { data } = await masterApi.get("/master/campanhas");
      setItems(data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const execNow = async (id, nome) => {
    const ok = await modal.confirm({
      title: "Executar campanha agora",
      message: `A campanha "${nome}" será disparada imediatamente para os clientes elegíveis.`,
      confirmText: "Executar agora",
    });
    if (!ok) return;
    try {
      const { data } = await masterApi.post(`/master/campanhas/${id}/executar-agora`, {});
      modal.success({
        title: "Campanha executada",
        message: `"${nome}" foi disparada.`,
        details: [
          { label: "Enviados", value: String(data.enviados) },
          { label: "Erros", value: String(data.erros) },
          { label: "Total", value: String(data.total) },
        ],
      });
      load();
    } catch (e) { modal.error(apiError(e)); }
  };

  const toggleAtivo = async (c) => {
    try { await masterApi.put(`/master/campanhas/${c.id}`, { ativo: !c.ativo }); load(); }
    catch (e) { modal.error(apiError(e)); }
  };

  const del = async (id) => {
    const ok = await modal.confirm({
      title: "Excluir campanha",
      message: "Esta campanha será removida permanentemente. Esta ação não pode ser desfeita.",
      confirmText: "Excluir",
      tone: "danger",
    });
    if (!ok) return;
    try { await masterApi.delete(`/master/campanhas/${id}`); modal.success("Campanha excluída com sucesso."); load(); }
    catch (e) { modal.error(apiError(e)); }
  };

  const preview = async (c) => {
    setPreviewing(c); setPreviewData(null);
    try { const { data } = await masterApi.get(`/master/campanhas/${c.id}/preview`); setPreviewData(data); }
    catch (e) { modal.error(apiError(e)); setPreviewing(null); }
  };

  const loadHistory = async (c) => {
    setHistoryOf(c); setHistory([]);
    try { const { data } = await masterApi.get(`/master/campanhas/${c.id}/historico`); setHistory(data); }
    catch (e) { modal.error(apiError(e)); setHistoryOf(null); }
  };

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-campanhas">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="chip mb-1.5 text-[10px] py-0.5">Recorrentes</div>
          <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Campanhas</h1>
        </div>
        <button data-testid="nova-campanha-btn" onClick={() => { setEditing(null); setOpen(true); }}
                className="btn-action btn-novo">
          <Plus size={13} /> Nova Campanha
        </button>
      </div>

      <div className="text-[11px] text-neutral-600 px-3 py-2.5 rounded-lg border border-dashed border-neutral-300 bg-white/60">
        Rodam automaticamente no horário (fuso America/Sao_Paulo) pelo <b>canal escolhido na campanha (SMS ou WhatsApp)</b>. Variáveis disponíveis: <code>{"{nome}"}</code>, <code>{"{desconto}"}</code>, <code>{"{telefone}"}</code>, <code>{"{unidade}"}</code>, <code>{"{valor}"}</code> (saldo de cashback). No <b>SMS</b> a mensagem sai sem acentos e é cortada em <b>160 caracteres</b>. Acompanhe as execuções no <b>Histórico</b> de cada campanha.
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Nome</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Segmento</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Frequência</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Última Exec.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Envios</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ativo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`campanha-row-${c.id}`}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell className="text-xs">{SEGMENTOS[c.segmento] || c.segmento}</TableCell>
                <TableCell className="text-xs">
                  {FREQUENCIAS[c.frequencia]}
                  {c.frequencia === "semanal" && ` · ${DIAS_SEMANA[c.dia_semana] || "?"}`}
                  {c.frequencia === "mensal" && ` · dia ${c.dia_mes}`}
                  {` · ${String(c.hora).padStart(2, "0")}:${String(c.minuto).padStart(2, "0")}`}
                </TableCell>
                <TableCell className="text-xs">{c.unit_id === "all" ? "Todas" : (unidades.find((u) => u.id === c.unit_id)?.nome || "—")}</TableCell>
                <TableCell className="text-xs">{c.ultima_execucao ? new Date(c.ultima_execucao).toLocaleString("pt-BR") : "—"}</TableCell>
                <TableCell>{c.total_envios || 0}</TableCell>
                <TableCell><Switch data-testid={`camp-switch-${c.id}`} checked={c.ativo} onCheckedChange={() => toggleAtivo(c)} /></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button data-testid={`camp-exec-${c.id}`} onClick={() => execNow(c.id, c.nome)}
                            title="Executar agora" className="icon-action icon-action--green"><Play size={14} /></button>
                    <button data-testid={`camp-preview-${c.id}`} onClick={() => preview(c)}
                            title="Preview" className="icon-action"><Eye size={14} /></button>
                    <button data-testid={`camp-hist-${c.id}`} onClick={() => loadHistory(c)}
                            title="Histórico" className="icon-action"><History size={14} /></button>
                    <button data-testid={`camp-edit-${c.id}`} onClick={() => { setEditing(c); setOpen(true); }}
                            className="icon-action icon-action--edit"><Pencil size={14} /></button>
                    <button data-testid={`camp-del-${c.id}`} onClick={() => del(c.id)}
                            className="icon-action icon-action--danger"><Trash2 size={14} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-6">Nenhuma campanha.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl border-none p-0 max-h-[90vh] overflow-auto">
          <div className="bg-white p-5 rounded-2xl">
            <CampanhaForm unidades={unidades} campanha={editing} onDone={() => { setOpen(false); load(); }} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewing} onOpenChange={(v) => !v && setPreviewing(null)}>
        <DialogContent className="max-w-sm rounded-2xl border-none p-0">
          <div className="bg-white p-5 rounded-2xl">
            <DialogHeader><DialogTitle className="font-serif-display text-xl">Preview · {previewing?.nome}</DialogTitle></DialogHeader>
            {previewData ? (
              <div className="mt-2">
                <div className="font-serif-display text-4xl">{previewData.total}</div>
                <div className="text-xs text-neutral-500 mb-2">clientes seriam alcançados</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Amostra</div>
                <ul className="text-xs space-y-0.5">
                  {previewData.amostra.map((c, i) => <li key={i}>• {c.nome} — {c.telefone}</li>)}
                </ul>
              </div>
            ) : <div className="text-sm">Calculando...</div>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="max-w-2xl rounded-2xl border-none p-0">
          <div className="bg-white p-5 rounded-2xl">
            <DialogHeader><DialogTitle className="font-serif-display text-xl">Histórico · {historyOf?.nome}</DialogTitle></DialogHeader>
            <Table>
              <TableHeader><TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Quando</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Tipo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Segmento</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Enviados</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Erros</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {history.map((h, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{new Date(h.executado_em).toLocaleString("pt-BR")}</TableCell>
                    <TableCell className="text-xs">{h.tipo}</TableCell>
                    <TableCell>{h.total_segmento}</TableCell>
                    <TableCell className="font-semibold text-rose-600">{h.enviados}</TableCell>
                    <TableCell>{h.erros}</TableCell>
                  </TableRow>
                ))}
                {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-6">Nenhuma execução ainda.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CampanhaForm({ unidades, campanha, onDone }) {
  const [f, setF] = useState({
    nome: campanha?.nome || "",
    segmento: campanha?.segmento || "sumidos",
    dias_segmento: campanha?.dias_segmento ?? 30,
    min_abastecimentos: campanha?.min_abastecimentos ?? 4,
    mensagem_template: campanha?.mensagem_template || "Ola {nome}! Volte ao posto e ganhe {desconto} centavos/L de desconto no proximo abastecimento.",
    aplicar_cupom_saudade: campanha?.aplicar_cupom_saudade ?? true,
    desconto_centavos_litro: campanha?.desconto_centavos_litro ?? 15,
    frequencia: campanha?.frequencia || "semanal",
    hora: campanha?.hora ?? 10,
    minuto: campanha?.minuto ?? 0,
    dia_semana: campanha?.dia_semana ?? 0,
    dia_mes: campanha?.dia_mes ?? 1,
    unit_id: campanha?.unit_id || "all",
    ativo: campanha?.ativo ?? true,
    incluir_saldo_cashback: campanha?.incluir_saldo_cashback ?? true,
    canal: campanha?.canal || "sms",
  });
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    if (!f.nome) return modal.error("Nome obrigatório");
    if (!f.mensagem_template) return modal.error("Mensagem obrigatória");
    try {
      if (campanha) await masterApi.put(`/master/campanhas/${campanha.id}`, f);
      else await masterApi.post("/master/campanhas", f);
      modal.success(campanha ? "Campanha atualizada" : "Campanha criada");
      onDone();
    } catch (e) { modal.error(apiError(e)); }
  };

  return (
    <div className="space-y-3">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-xl">{campanha ? "Editar Campanha" : "Nova Campanha"}</DialogTitle>
      </DialogHeader>
      <Field label="Nome"><input data-testid="camp-nome" value={f.nome} onChange={(e) => set("nome", e.target.value)} className={inputCls} /></Field>

      <Field label="Segmento">
        <select data-testid="camp-segmento" value={f.segmento} onChange={(e) => set("segmento", e.target.value)} className={inputCls}>
          {Object.entries(SEGMENTOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>

      {f.segmento === "sumidos" && (
        <Field label="Dias sem abastecer">
          <input type="number" data-testid="camp-dias" value={f.dias_segmento} onChange={(e) => set("dias_segmento", parseInt(e.target.value) || 30)} className={inputCls} />
        </Field>
      )}
      {(f.segmento === "frequentes" || f.segmento === "low_freq") && (
        <Field label="Limite de abastecimentos">
          <input type="number" data-testid="camp-minab" value={f.min_abastecimentos} onChange={(e) => set("min_abastecimentos", parseInt(e.target.value) || 4)} className={inputCls} />
        </Field>
      )}

      <Field label="Canal de envio">
        <select data-testid="camp-canal" value={f.canal} onChange={(e) => set("canal", e.target.value)} className={inputCls}>
          <option value="sms">SMS</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="ambos">SMS + WhatsApp</option>
        </select>
      </Field>

      <Field label="Mensagem (vars: {nome} {desconto} {telefone} {unidade} {valor})">
        <textarea data-testid="camp-msg" value={f.mensagem_template} onChange={(e) => set("mensagem_template", e.target.value)}
                  rows={3}
                  className="w-full rounded-lg bg-white border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition" />
        {(f.canal === "sms" || f.canal === "ambos") && (() => {
          // Mede como o SMS realmente sai: sem acentos (NFD) — corte duro em 160 no envio.
          const ascii = (f.mensagem_template || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^\x00-\x7F]/g, "");
          const n = ascii.length;
          return (
            <div data-testid="camp-msg-sms-len"
                 className={`text-[10px] mt-1 ${n > 160 ? "text-rose-600 font-semibold" : "text-neutral-500"}`}>
              SMS: {n}/160 caracteres (sem acentos){n > 160 ? " — será CORTADA no envio!" : ""}
              {"  "}· variáveis podem aumentar o tamanho final
            </div>
          );
        })()}
      </Field>

      <Toggle label="Aplicar cupom saudade" hint="Marca cliente com cupom ativo para próximo abastecimento"
              checked={f.aplicar_cupom_saudade} onChange={(v) => set("aplicar_cupom_saudade", v)} testid="camp-saudade" />

      <Toggle label="Anexar saldo de cashback" hint="Adiciona o saldo de cashback do cliente à mensagem (boost de retorno)"
              checked={f.incluir_saldo_cashback} onChange={(v) => set("incluir_saldo_cashback", v)} testid="camp-cashback-suffix" />

      {f.aplicar_cupom_saudade && (
        <Field label="Desconto (¢/L)">
          <input type="number" step="0.5" data-testid="camp-desc" value={f.desconto_centavos_litro}
                 onChange={(e) => set("desconto_centavos_litro", parseFloat(e.target.value) || 0)} className={inputCls} />
        </Field>
      )}

      <Field label="Frequência">
        <select data-testid="camp-freq" value={f.frequencia} onChange={(e) => set("frequencia", e.target.value)} className={inputCls}>
          {Object.entries(FREQUENCIAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>

      {f.frequencia === "semanal" && (
        <Field label="Dia da semana">
          <select data-testid="camp-dow" value={String(f.dia_semana)} onChange={(e) => set("dia_semana", parseInt(e.target.value))} className={inputCls}>
            {DIAS_SEMANA.map((d, i) => <option key={i} value={String(i)}>{d}</option>)}
          </select>
        </Field>
      )}
      {f.frequencia === "mensal" && (
        <Field label="Dia do mês (1-28)">
          <input type="number" min={1} max={28} data-testid="camp-dom" value={f.dia_mes}
                 onChange={(e) => set("dia_mes", parseInt(e.target.value) || 1)} className={inputCls} />
        </Field>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Field label="Hora">
          <input type="number" min={0} max={23} data-testid="camp-hora" value={f.hora}
                 onChange={(e) => set("hora", parseInt(e.target.value) || 0)} className={inputCls} />
        </Field>
        <Field label="Minuto">
          <input type="number" min={0} max={59} data-testid="camp-min" value={f.minuto}
                 onChange={(e) => set("minuto", parseInt(e.target.value) || 0)} className={inputCls} />
        </Field>
      </div>

      <Field label="Unidade">
        <select data-testid="camp-unit" value={f.unit_id} onChange={(e) => set("unit_id", e.target.value)} className={inputCls}>
          <option value="all">Todas</option>
          {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>
      </Field>

      <Toggle label="Ativa" checked={f.ativo} onChange={(v) => set("ativo", v)} testid="camp-ativo" />

      <button data-testid="camp-submit" onClick={submit}
              className="btn-action btn-salvar w-full">
        <Save size={15} /> {campanha ? "Salvar" : "Criar Campanha"}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white p-2.5">
      <div>
        <div className="text-[12px] font-medium">{label}</div>
        {hint && <div className="text-[10px] text-neutral-500 mt-0.5">{hint}</div>}
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
